import React, { ChangeEvent, useEffect, useState } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { WarningMessage } from '@eika/attention-message';
import { LinkButton, SmallButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { Combobox } from '@eika/combobox';
import { IconTypes } from '@eika/icon';
import { Input } from '@eika/input';

import { useReceiverResidenceCountries } from '../../fetchHooks/useCountryDetails';
import { useDeleteCrossBorderReceiver } from '../../fetchHooks/useCrossBorderReceivers';
import type { CrossBorderReceiver } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import { Content } from '../shared/Content';
import { StickyFooterRow } from '../shared/StickyFooter';
import NewCrossBorderReceiverSkeleton from '../transaction/CrossBorderPayment/Receiver/NewCrossBorderReceiverSkeleton';
import { MAX_ADDRESS_LENGTH } from '../transaction/CrossBorderPayment/form/state';
import { checkForNumbersInName } from '../transaction/CrossBorderPayment/form/validate';
import BankDetails from './BankDetails';
import CrossBorderReceiverResult from './CrossBorderReceiverResult';
import ReceiverAction from './ReceiverActionType';
import useUpdateReceiver from './useUpdateReceiver';

import './CrossBorderReceiversDetails.scss';

interface Props {
    receiver: CrossBorderReceiver;
    onClose: () => void;
    onEdit: () => void;
}

const CrossBorderReceiversDetails: React.FC<Props> = ({ receiver, onClose, onEdit }) => {
    const translated = useTranslation();
    const [showDeleteDialogue, setShowDeleteDialogue] = useState(false);
    const [deleteReceiverSpinner, setDeleteReceiverSpinner] = useState(false);
    const [deleteReceiverError, setDeleteReceiverError] = useState<RequestExceptionWithResponse>();
    const [receiverAction, setReceiverAction] = useState<ReceiverAction>();
    const {
        receiverName,
        receiverAddress,
        receiverAddress2,
        receiverCountryName,
        receiverCountryCode,
        update,
        hasStateChange,
    } = useUpdateReceiver(receiver, onEdit, setReceiverAction);
    const handleReceiverNameChange = (event: ChangeEvent<HTMLInputElement>) => receiverName.set(event.target.value);
    const handleReceiverAddressChange = (event: ChangeEvent<HTMLInputElement>) =>
        receiverAddress.set(event.target.value);
    const handleReceiverAddress2Change = (event: ChangeEvent<HTMLInputElement>) =>
        receiverAddress2.set(event.target.value);
    const { data: receiverCountryList, isLoading: loadingReceiverCountries } = useReceiverResidenceCountries();
    const showNumbersInNameInfoMessage = checkForNumbersInName(receiverName.value ?? '');

    const deleteReceiver = useDeleteCrossBorderReceiver(setDeleteReceiverSpinner, setDeleteReceiverError);

    useEffect(() => {
        const receiverCountry = receiverCountryList?.find(
            (country) => country.countryCode.value === receiverCountryCode.value?.value,
        );
        receiverCountryName.set((prev) => receiverCountry?.name ?? prev);
    }, [receiverCountryList, receiverCountryCode.value, receiverCountryName]);

    const handleCountrySelection = (value: string) => {
        const chosenCountry = receiverCountryList?.find(
            (country) => country.name.toLowerCase() === value.toLowerCase(),
        );
        receiverCountryName.set(chosenCountry?.name ?? value);
        receiverCountryCode.set(chosenCountry?.countryCode);
    };

    const handleDelete = () => {
        setReceiverAction(ReceiverAction.DELETE);
        setDeleteReceiverSpinner(true);
        deleteReceiver.mutate(receiver.id);
        onEdit();
    };

    if (receiverAction === ReceiverAction.DELETE || receiverAction === ReceiverAction.EDIT) {
        return (
            <CrossBorderReceiverResult
                receiverAction={receiverAction}
                retryDelete={handleDelete}
                retryChange={update.mutate}
                deleteReceiverError={deleteReceiverError}
                updateReceiverError={update.error.value}
            />
        );
    }

    if (showDeleteDialogue) {
        return (
            <Content className="cross-border-receiver__delete-dialogue">
                <h2 className="typography-h3 cross-border-receiver-dialogue__title">
                    {translated.confirmDeleteReceiver}
                </h2>
                <div className="cross-border-receiver-dialogue__button-row">
                    <LinkButton onClick={() => setShowDeleteDialogue(false)} disabled={deleteReceiverSpinner}>
                        {translated.cancel}
                    </LinkButton>
                    <ButtonWithSpinner
                        size="small"
                        buttonType="positive"
                        onClick={handleDelete}
                        showSpinner={deleteReceiverSpinner}
                        data-testid="confirm-cross-border-receiver-deletion-button"
                    >
                        {translated.confirmDelete}
                    </ButtonWithSpinner>
                </div>
            </Content>
        );
    }

    return (
        <form onSubmit={update.mutate}>
            <h2>{translated.details}</h2>
            <Input
                className="cross-border-receiver-details__margin"
                label={translated.name}
                value={receiverName.value}
                onChange={handleReceiverNameChange}
                error={update.validation.receiverNameInput}
            />
            {showNumbersInNameInfoMessage && (
                <WarningMessage
                    className="cross-border-payment__warning-message"
                    icon={IconTypes.INFO_24}
                    text={translated.crossBorderPayment.numbersInNameInfoMessage}
                />
            )}
            {receiver.address && (
                <>
                    <div className="cross-border-receiver-details__address">
                        <Input
                            className="cross-border-receiver-details__address1"
                            label={translated.crossBorderPayment.newReceiver.receiverAddress}
                            value={receiverAddress.value}
                            maxLength={MAX_ADDRESS_LENGTH}
                            onChange={handleReceiverAddressChange}
                            error={update.validation.addressInput}
                        />
                        <Input
                            maxLength={MAX_ADDRESS_LENGTH}
                            value={receiverAddress2.value}
                            onChange={handleReceiverAddress2Change}
                        />
                    </div>
                </>
            )}
            {loadingReceiverCountries && <NewCrossBorderReceiverSkeleton />}
            {receiverCountryList && (
                <Combobox
                    id="receiver-details-country-select"
                    label={translated.crossBorderPayment.newReceiver.countryLabel}
                    suggestions={receiverCountryList?.map((country) => {
                        return { value: country.name };
                    })}
                    value={receiverCountryName.value ?? ''}
                    onChange={(value) => {
                        handleCountrySelection(value);
                    }}
                    onSelect={(value) => handleCountrySelection(value.toString())}
                    error={update.validation.countryInput}
                />
            )}
            <BankDetails receiver={receiver} receiverCountryName={receiverCountryName.value} />
            <div className="cross-border-receiver-details__delete-btn">
                <SmallButton
                    buttonType="negative"
                    onClick={() => setShowDeleteDialogue(true)}
                    icon={IconTypes.SLETT_24}
                    hidden={showDeleteDialogue}
                    data-testid="cross-border-receiver-delete-button"
                >
                    {translated.delete}
                </SmallButton>
            </div>
            {hasStateChange && (
                <StickyFooterRow>
                    <LinkButton onClick={onClose} disabled={update.loading.value}>
                        {translated.cancel}
                    </LinkButton>
                    <ButtonWithSpinner
                        buttonType="positive"
                        type="submit"
                        showSpinner={update.loading.value}
                        onClick={update.mutate}
                    >
                        {translated.save}
                    </ButtonWithSpinner>
                </StickyFooterRow>
            )}
        </form>
    );
};

export default CrossBorderReceiversDetails;
